import { useContext } from "react";

import { allTemplatesSplit, UseTemplates, WsTemplateExtended } from "@/components/Templates/templates";
import { client } from "@/config.ts";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { bulkUpsertTemplate, cachedTemplatesSelect, deleteTemplateById } from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { UserContext } from "@/models/StateProviders/userProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";

export default function useTemplates(): UseTemplates {
  const { ampli } = useContext(TrackingContext);
  const { user, workspaceMembership } = useContext(UserContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const templates: WsTemplateExtended[] =
    useReactiveSelectQuery(cachedTemplatesSelect, [{ workspaceId: currentWorkspaceId }], "FleetMessaging:templates") ??
    [];
  const canEditOthers = workspaceMembership.role === "admin";

  const saveTemplate = async ({ name, template }: { name: string; template: string }) => {
    try {
      const response = await client.createWorkspaceTemplate({
        workspaceId: currentWorkspaceId,
        name,
        template,
      });
      await bulkUpsertTemplate([response?.template]);
      ampli.createTemplate();
      return {
        ...response?.template,
        canEdit: true,
        authorName: user.name,
        role: workspaceMembership.role,
      };
    } catch {
      return Promise.reject({ message: "Could not save template. Please try again." });
    }
  };

  const deleteTemplate = async (id: string): Promise<null> => {
    try {
      await client.deleteWorkspaceTemplate({ workspaceId: currentWorkspaceId, templateId: id });
      await deleteTemplateById(id);
      ampli.deleteTemplate();
      return null;
    } catch {
      return Promise.reject({ message: "Could not delete template. Please try again." });
    }
  };

  const updateTemplate = async ({ id, name, template }: { id: string; name: string; template: string }) => {
    try {
      const response = await client.updateWorkspaceTemplate({
        workspaceId: currentWorkspaceId,
        templateId: id,
        name,
        template,
      });
      await bulkUpsertTemplate([response?.template]);
      ampli.saveTemplate();
      return {
        ...response?.template,
        canEdit: true,
        authorName: user.name,
        role: workspaceMembership.role,
      };
    } catch {
      return Promise.reject({ message: "Could not update template. Please try again." });
    }
  };

  const cloneTemplate = async (id: string) => {
    const foundTemplate = templates?.find((t) => t.id === id);
    if (!foundTemplate) {
      return Promise.reject({ message: "Template not found." });
    }
    try {
      const response = await client.createWorkspaceTemplate({
        workspaceId: currentWorkspaceId,
        name: `(CLONED) ${foundTemplate.name}`,
        template: foundTemplate.template,
      });
      await bulkUpsertTemplate([response?.template]);
      ampli.cloneTemplate();
      return {
        ...response?.template,
        canEdit: true,
        authorName: user.name,
        role: workspaceMembership.role,
        cloned: true,
      };
    } catch {
      return Promise.reject({ message: "Could not clone template. Please try again." });
    }
  };

  return {
    count: templates?.length || 0,
    templates: allTemplatesSplit({ templates, myAccountId, canEditOthers }),
    cloneTemplate,
    saveTemplate,
    deleteTemplate,
    updateTemplate,
  };
}
